import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ConsentState, ConsentActions, ConsentCategory } from './types';
import { ALL_CONSENT_CATEGORIES, EXTERNAL_ROLES } from './types';

const CONSENT_VERSION = 1;

type ConsentStore = ConsentState & ConsentActions;

function onConsentChange(granted: ConsentCategory[]): void {
  if (granted.includes('analytics')) {
    import('@/shared/analytics/clarity').then((m) => m.activateAfterConsent());
  }
}

export const useConsentStore = create<ConsentStore>()(
  persist(
    (set, get) => ({
      granted: ['necessary'],
      grantedAt: null,
      version: CONSENT_VERSION,
      hasResponded: false,

      grantConsent: (categories: ConsentCategory[]): void => {
        const unique = [...new Set([...get().granted, ...categories])];
        set({
          granted: unique,
          grantedAt: new Date().toISOString(),
          version: CONSENT_VERSION,
          hasResponded: true,
        });
        onConsentChange(unique);
      },

      grantAll: (): void => {
        set({
          granted: [...ALL_CONSENT_CATEGORIES],
          grantedAt: new Date().toISOString(),
          version: CONSENT_VERSION,
          hasResponded: true,
        });
        onConsentChange(ALL_CONSENT_CATEGORIES);
      },

      hasConsent: (category: ConsentCategory): boolean => {
        return get().granted.includes(category);
      },

      needsConsent: (): boolean => {
        // If user already responded, no need to show banner
        if (get().hasResponded) return false;

        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (!authStorage) return false;

          const parsed = JSON.parse(authStorage) as {
            state?: { user?: { roles?: string[] } };
          };
          const roles = parsed?.state?.user?.roles ?? [];

          const isExternal = roles.some((r) =>
            EXTERNAL_ROLES.includes(r as (typeof EXTERNAL_ROLES)[number])
          );

          return isExternal;
        } catch {
          return false;
        }
      },

      revokeConsent: (categories: ConsentCategory[]): void => {
        const filtered = get().granted.filter((c) => !categories.includes(c) || c === 'necessary');
        set({ granted: filtered });
        onConsentChange(filtered);
      },

      resetConsent: (): void => {
        set({ granted: ['necessary'], grantedAt: null, hasResponded: false });
      },
    }),
    { name: 'cookie-consent' }
  )
);
